import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';

import { OpFloatButton } from 'new-components/DLS/OpFloatButton/OpFloatButton';
import { LightningBoltSvg } from 'components/svgs/LightningBoltSvg';
import { useSelectorJs } from 'utils/customHooks';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive } from 'utils/helpers';
import { useUrlSearchParams } from 'utils/customHooks/useUrlSearchParams';
import { useRemoveQueryParams } from 'utils/customHooks/useRemoveQueryParams';

import { DATE_TIME_FORMAT } from '../../../../constants/dates';
import { LocationsState } from '../../../../store/slices/locationsSlice';
import VisitorsDrawer from '../../../../pages/visitors/drawer/VisitorsDrawer';
import {
  fetchInvitationConfig,
  VisitorInvitationState,
} from '../../../../store/slices/visitorInvitationSlice';
import { setSelectedVisit } from '../../../../store/slices/visitsSlice';
import { AppDispatch, ImmutableRootState } from '../../../../store/store';
import { VisitorSearch } from '../../../../types/visitorSearchTypes';
import { LocationWorkflowResponse } from '../../../../types/visitorsDrawerTypes';
import { VisitorWorkflowConfig } from '../../../../types/visitorWorkflowTypes';
import QuickActionsModal from '../quickActions/QuickActionsModal';

import InvitationDrawer from './invite/InvitationDrawer';
import ScanIDModal from './signIn/ScanIDModal';
import ScanQRCodeModal from './signIn/ScanQRCodeModal';
import SelectLocationModal from './SelectLocationModal';
import SignInManuallyDrawer from './signIn/SignInManuallyDrawer';
import SignInMethodModal from './signIn/SignInMethodModal';
import SignInModal from './signIn/SignInModal';
import SignOutModal from './signOut/SignOutModal';

import 'routes/AuthenticatedContainer/AuthenticatedContainer.scss';

type Actions = 'SIGN_IN' | 'SIGN_OUT' | 'NEW_INVITATION';

interface SignInWorkflowConfigResponse {
  data?: VisitorWorkflowConfig[] | null;
}

interface SearchVisitorsResponse {
  data?: VisitorSearch[] | null;
}

const ActionFloatButtons = () => {
  const tagPrefix = 'quick-actions-modal';
  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();
  const { isValid } = useValidateScopes([
    'o{orgId}-visitorInvite:w',
    'o{orgId}-visitorSignIn:w',
    'o{orgId}-visitorSignOut:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);
  const invitationConfig = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorInvitation', 'invitationConfig']),
  ) as VisitorInvitationState['invitationConfig'];
  const locations = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'locations']),
  ) as LocationsState['locations'];

  const [isSelectLocationModalOpen, setIsSelectLocationModalOpen] =
    useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<Actions | null>(null);
  const [visitorsToday, setVisitorsToday] = useState<VisitorSearch[]>([]);
  const [visitorNameFilter, setVisitorNameFilter] = useState<
    { firstName: string; middleName: string; lastName: string } | undefined
  >(undefined);
  const [newVisitorName, setNewVisitorName] = useState<
    | { firstName: string; middleName: string; lastName: string; dob?: string }
    | undefined
  >(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [signInWorkflowConfig, setSignInWorkflowConfig] = useState<
    VisitorWorkflowConfig | undefined
  >(undefined);
  const [signInMethodLoading, setSignInMethodLoading] =
    useState<boolean>(false);
  const [isQuickActionsModalOpen, setIsQuickActionsModalOpen] =
    useState<boolean>(false);
  // sign in
  const [isSignInMethodModalOpen, setIsSignInMethodModalOpen] =
    useState<boolean>(false);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState<boolean>(false);
  const [isSignInManuallyDrawerOpen, setIsSignInManuallyDrawerOpen] =
    useState<boolean>(false);
  const [isScanIDModalOpen, setIsScanIDModalOpen] = useState<boolean>(false);
  const [isScanQRCodeModalOpen, setIsScanQRCodeModalOpen] =
    useState<boolean>(false);
  // sign out
  const [isSignOutModalOpen, setIsSignOutModalOpen] = useState<boolean>(false);
  // invite
  const [isNewInvitationDrawerOpen, setIsNewInvitationDrawerOpen] =
    useState<boolean>(false);
  // visitors drawer
  const [isVisitorsDrawerOpen, setIsVisitorsDrawerOpen] =
    useState<boolean>(false);
  const { visitorId: _visitorId } = useUrlSearchParams();
  const removeQueryParams = useRemoveQueryParams();

  const locationIdRef = useRef<number | null>(null);

  useEffect(() => {
    // Only fetch when the user has the scopes that render this component (see
    // the `if (!isValid) return null` guard below). Without this check, the
    // effect still runs for unauthorized users (hooks execute regardless of the
    // early return) and GET /visitorInvitationConfig responds 403.
    if (!isValid) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        if (invitationConfig === null) {
          dispatch(fetchInvitationConfig({ orgId }));
        }
      } catch {
        // Failed to fetch data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orgId, dispatch, invitationConfig, isValid]);

  const handleFloatButtonClick = async (type: Actions) => {
    if (type === 'SIGN_IN') {
      // Open the method modal immediately in a loading state so the user gets
      // instant feedback, then fetch the workflow config to populate it.
      setSignInWorkflowConfig(undefined);
      setSignInMethodLoading(true);
      setIsSignInMethodModalOpen(true);

      try {
        const locationWorkflowResponse = (await heliumReceive(
          'locationConfig',
          [orgId, locationIdRef.current],
        )) as LocationWorkflowResponse;
        const signInWorkflowConfigResponse = (await heliumReceive(
          'getVisitorWorkflowConfig',
          [orgId, locationWorkflowResponse?.data?.[0]?.SignInWorkflowId],
        )) as SignInWorkflowConfigResponse;
        const workflowConfig = signInWorkflowConfigResponse.data?.[0];
        setSignInWorkflowConfig(workflowConfig);

        // With only a single sign-in method (no ID scan / pre-registration),
        // skip the chooser and go straight to the visitor sign-in modal.
        if (
          !(
            (workflowConfig?.scan === 1 && workflowConfig?.scanScanner === 1) ||
            invitationConfig?.allowPreRegistration === 1
          )
        ) {
          setIsSignInMethodModalOpen(false);
          setIsSignInModalOpen(true);
        }
      } catch {
        setIsSignInMethodModalOpen(false);
        setIsSignInModalOpen(true);
      } finally {
        setSignInMethodLoading(false);
      }
    } else if (type === 'SIGN_OUT') {
      setIsSignOutModalOpen(true);
    } else if (type === 'NEW_INVITATION') {
      setIsNewInvitationDrawerOpen(true);
    }
  };

  const handleLocationSelect = async (locationId: number) => {
    locationIdRef.current = locationId;
    setIsSelectLocationModalOpen(false);

    // Kick off the pending action right away so the sign-in modal opens without
    // waiting on the today's-visits fetch below.
    if (pendingAction) {
      handleFloatButtonClick(pendingAction);
      setPendingAction(null);
    }

    // Fetch visits for today for the selected location (used later by scan modals)
    try {
      const selectedLocation = locations.find((loc) => loc.id === locationId);
      const tz = selectedLocation?.timezone || 'UTC';
      const startDate = dayjs()
        .tz(tz)
        .startOf('day')
        .utc()
        .format(DATE_TIME_FORMAT);
      const endDate = dayjs()
        .tz(tz)
        .endOf('day')
        .utc()
        .format(DATE_TIME_FORMAT);

      const response = (await heliumReceive('getSearchVisitors', [orgId], {
        locationId: locationIdRef.current,
        startDate,
        endDate,
      })) as SearchVisitorsResponse;
      setVisitorsToday(response.data ?? []);
    } catch {
      // Failed to fetch visits for today
    }
  };

  // Don't render footer at all if user doesn't have required scopes
  // This prevents child modals/drawers from mounting and making API calls
  if (!isValid) {
    return null;
  }

  return (
    <footer className="op-nav__footer">
      <div className="op-nav__footer-space" />
      {!loading && isValid && (
        <OpFloatButton
          className="authenticated-container__quick-actions-button"
          icon={<LightningBoltSvg />}
          onClick={() => setIsQuickActionsModalOpen(true)}
          testId="quick-actions-button"
          gtm={`${tagPrefix}-open`}
        />
      )}

      <QuickActionsModal
        open={isQuickActionsModalOpen}
        onClose={() => setIsQuickActionsModalOpen(false)}
      />
      {isSelectLocationModalOpen && (
        <SelectLocationModal
          open={isSelectLocationModalOpen}
          onClose={() => setIsSelectLocationModalOpen(false)}
          handleLocationSelect={handleLocationSelect}
        />
      )}
      {isSignInMethodModalOpen && locationIdRef.current !== null && (
        <SignInMethodModal
          open={isSignInMethodModalOpen}
          onClose={() => setIsSignInMethodModalOpen(false)}
          openSignInModal={() => setIsSignInModalOpen(true)}
          openScanIDModal={() => setIsScanIDModalOpen(true)}
          openScanQRCodeModal={() => setIsScanQRCodeModalOpen(true)}
          openSignInManuallyDrawer={() => setIsSignInManuallyDrawerOpen(true)}
          locationIdRef={locationIdRef.current}
          loading={signInMethodLoading}
          signInWorkflowConfig={signInWorkflowConfig}
        />
      )}
      {isSignInModalOpen && locationIdRef.current !== null && (
        <SignInModal
          open={isSignInModalOpen}
          locationIdRef={locationIdRef.current}
          onClose={() => {
            setIsSignInModalOpen(false);
            setVisitorNameFilter(undefined);
          }}
          openVisitorsDrawer={() => setIsVisitorsDrawerOpen(true)}
          openSignInManuallyDrawer={() => setIsSignInManuallyDrawerOpen(true)}
          visitorNameFilter={visitorNameFilter}
        />
      )}
      {isSignInManuallyDrawerOpen && locationIdRef.current !== null && (
        <SignInManuallyDrawer
          open={isSignInManuallyDrawerOpen}
          locationIdRef={locationIdRef.current}
          onClose={() => {
            setNewVisitorName(undefined);
            setVisitorNameFilter(undefined);
            setIsSignInManuallyDrawerOpen(false);
          }}
          newVisitorName={newVisitorName}
          setNewVisitorName={setNewVisitorName}
        />
      )}
      {isScanIDModalOpen && (
        <ScanIDModal
          open={isScanIDModalOpen}
          visitorsToday={visitorsToday}
          onClose={() => setIsScanIDModalOpen(false)}
          openVisitorsDrawer={() => setIsVisitorsDrawerOpen(true)}
          openSelectVisitorsModal={() => setIsSignInModalOpen(true)}
          openSignInManuallyDrawer={() => setIsSignInManuallyDrawerOpen(true)}
          setVisitorNameFilter={setVisitorNameFilter}
          setNewVisitorName={setNewVisitorName}
        />
      )}
      {isScanQRCodeModalOpen && (
        <ScanQRCodeModal
          open={isScanQRCodeModalOpen}
          visitorsToday={visitorsToday}
          onClose={() => setIsScanQRCodeModalOpen(false)}
          openVisitorsDrawer={() => setIsVisitorsDrawerOpen(true)}
        />
      )}
      {isSignOutModalOpen && locationIdRef.current !== null && (
        <SignOutModal
          open={isSignOutModalOpen}
          locationIdRef={locationIdRef.current}
          onClose={() => setIsSignOutModalOpen(false)}
          openVisitorsDrawer={() => setIsVisitorsDrawerOpen(true)}
        />
      )}
      {isNewInvitationDrawerOpen && locationIdRef.current !== null && (
        <InvitationDrawer
          open={isNewInvitationDrawerOpen}
          onClose={() => {
            setIsNewInvitationDrawerOpen(false);
          }}
          locationIdRef={locationIdRef.current}
        />
      )}
      {isVisitorsDrawerOpen && (
        <VisitorsDrawer
          open={isVisitorsDrawerOpen}
          onClose={() => {
            setNewVisitorName(undefined);
            setVisitorNameFilter(undefined);
            setIsVisitorsDrawerOpen(false);
            dispatch(setSelectedVisit(null));
            removeQueryParams(['drawer', 'visitId', 'visitorId']);
          }}
        />
      )}
      <div className="op-nav__copyright">© {new Date().getFullYear()}</div>
    </footer>
  );
};

export default ActionFloatButtons;
